import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Switch } from '@moneyskyline/ui';
import { useMe, useUpdateMe } from '@/api/hooks/useMe';
import { useTransactions } from '@/api/hooks/useTransactions';
import { isErrorEnvelope } from '@/api/envelope';
import { useMoneyFormat } from '@/lib/money';
import { ReceiptEmailCard } from './ReceiptEmailCard';
import { AccountantAccess } from './AccountantAccess';

const CONFIRMED_TIMEZONE_COPY = 'Your saved timezone controls transaction calendar ranges and day grouping.';
const UNAVAILABLE_TIMEZONE_COPY = "Transaction calendar grouping in your saved timezone isn't available yet.";
const TIMEZONE_ERROR = 'Enter a valid timezone such as America/Los_Angeles.';
const SAVE_ERROR = 'Settings could not be saved. Try again.';

function isTimezoneValidationError(error: unknown): boolean {
  if (!isAxiosError(error) || !isErrorEnvelope(error.response?.data)) return false;
  if (error.response.data.error.code !== 'validation_error') return false;
  const details = error.response.data.error.details;
  if (!details || typeof details !== 'object' || !('errors' in details) || !Array.isArray(details.errors)) return false;
  return details.errors.some((entry) => {
    if (!entry || typeof entry !== 'object' || !('loc' in entry) || !Array.isArray(entry.loc)) return false;
    return entry.loc.includes('timezone');
  });
}

export function SettingsPage() {
  const meQuery = useMe();
  const updateMe = useUpdateMe();
  const transactionsQuery = useTransactions({ enabled: Boolean(meQuery.data) });
  const { masked, setMasked } = useMoneyFormat();
  const [draft, setDraft] = useState<{ email?: string; timezone?: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [timezoneError, setTimezoneError] = useState(false);
  const [saved, setSaved] = useState(false);

  const serverEmail = meQuery.data?.email ?? '';
  const serverTimezone = meQuery.data?.timezone ?? '';
  const email = draft?.email ?? serverEmail;
  const timezone = draft?.timezone ?? serverTimezone;
  const timezoneCopy = transactionsQuery.temporal.availability === 'confirmed'
    ? CONFIRMED_TIMEZONE_COPY
    : UNAVAILABLE_TIMEZONE_COPY;

  const updateField = (field: 'email' | 'timezone', value: string) => {
    const serverValue = field === 'email' ? serverEmail : serverTimezone;
    setDraft((current) => {
      const next = { ...(current ?? {}) };
      if (value === serverValue) delete next[field];
      else next[field] = value;
      return Object.keys(next).length > 0 ? next : null;
    });
    setFormError(null);
    setTimezoneError(false);
    setSaved(false);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    setTimezoneError(false);
    setSaved(false);
    const current = meQuery.data;
    if (!current) return;
    if (current.email !== null && email.trim() === '') {
      setFormError('Email cannot be cleared from Settings.');
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError('Enter a valid email address.');
      return;
    }
    const input: { email?: string; timezone?: string } = {};
    if (draft?.email !== undefined && email !== (current.email ?? '')) input.email = email;
    if (draft?.timezone !== undefined && timezone !== current.timezone) input.timezone = timezone;
    if (Object.keys(input).length === 0) return;
    try {
      await updateMe.mutateAsync(input);
      setDraft(null);
      setSaved(true);
    } catch (error) {
      const timezoneValidation = isTimezoneValidationError(error);
      setTimezoneError(timezoneValidation);
      setFormError(timezoneValidation ? TIMEZONE_ERROR : SAVE_ERROR);
    }
  };

  const hasChanges = Boolean(
    meQuery.data &&
      ((draft?.email !== undefined && email !== serverEmail) ||
        (draft?.timezone !== undefined && timezone !== serverTimezone)),
  );

  return (
    <section className="mx-auto w-full max-w-2xl space-y-6 p-4 sm:p-6" aria-labelledby="settings-heading">
      <div>
        <h1 id="settings-heading" tabIndex={-1} className="text-2xl font-bold">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Manage your profile and privacy on this device.</p>
      </div>

      {meQuery.isPending && (
        <div role="status" aria-live="polite" className="rounded-md border border-border p-4 text-sm text-muted-foreground">
          Loading your settings…
        </div>
      )}
      {meQuery.isError && (
        <div className="rounded-md border border-degraded-border bg-degraded-surface p-4 text-sm" role="alert">
          <p>Settings could not be loaded.</p>
          <button type="button" onClick={() => meQuery.refetch()} className="mt-3 min-h-11 rounded-md bg-primary px-4 text-primary-foreground">Retry</button>
        </div>
      )}

      {meQuery.data && (
        <>
          <form onSubmit={submit} className="space-y-5 rounded-lg border border-border p-4 sm:p-6">
            <fieldset disabled={updateMe.isPending} className="space-y-4">
              <legend className="text-lg font-semibold">Profile</legend>
              <div className="space-y-1.5">
                <label htmlFor="settings-email" className="text-sm font-medium">Email</label>
                <input id="settings-email" name="email" type="email" value={email} onChange={(event) => updateField('email', event.target.value)} className="min-h-11 w-full rounded-md border border-input bg-background px-3" autoComplete="email" />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="settings-timezone" className="text-sm font-medium">Timezone</label>
                <input id="settings-timezone" name="timezone" value={timezone} onChange={(event) => updateField('timezone', event.target.value)} aria-describedby={timezoneError ? 'settings-timezone-error' : undefined} className="min-h-11 w-full rounded-md border border-input bg-background px-3" autoComplete="off" />
                <p className="text-xs text-muted-foreground">Use an IANA timezone such as America/Los_Angeles.</p>
                <p className="text-xs text-muted-foreground">{timezoneCopy}</p>
              </div>
            </fieldset>
            {formError && <p id={timezoneError ? 'settings-timezone-error' : undefined} role="alert" className="rounded-md border border-negative-border bg-negative-surface p-3 text-sm text-negative-foreground">{formError}</p>}
            {saved && <p role="status" aria-live="polite" className="rounded-md border border-positive-border bg-positive-surface p-3 text-sm text-positive-foreground">Settings saved.</p>}
            <button type="submit" disabled={updateMe.isPending || !hasChanges} className="min-h-11 rounded-md bg-primary px-5 py-2 font-semibold text-primary-foreground disabled:opacity-50">{updateMe.isPending ? 'Saving…' : 'Save settings'}</button>
          </form>

          <ReceiptEmailCard />

          <section className="space-y-3 rounded-lg border border-border p-4 sm:p-6" aria-labelledby="privacy-heading">
            <h2 id="privacy-heading" className="text-lg font-semibold">Privacy</h2>
            <label className="flex min-h-11 items-center justify-between gap-3 text-sm">
              <span>
                Hide balances by default
                <span className="block text-xs text-muted-foreground">Masks amounts until you reveal them. This device only.</span>
              </span>
              <input type="checkbox" checked={masked} onChange={(event) => setMasked(event.target.checked)} className="size-5" aria-label="Hide balances on this device" />
            </label>
            <div className="flex min-h-11 items-center justify-between gap-3 text-sm">
              <span>
                Weekly digest
                <span className="block text-xs text-muted-foreground">Mondays · net worth change, items to review</span>
              </span>
              <Switch disabled aria-label="Weekly digest" />
            </div>
            <div className="flex min-h-11 items-center justify-between gap-3 text-sm">
              <span>
                Big-expense alert
                <span className="block text-xs text-muted-foreground">Any single charge over a threshold you set. Delivery isn’t available yet.</span>
              </span>
              <Switch disabled aria-label="Big-expense alert" />
            </div>
            <p className="text-xs text-muted-foreground" role="status">
              Notification preferences aren’t saved yet. No digest or alert was scheduled.
            </p>
          </section>

          <AccountantAccess />

          <section className="space-y-3 rounded-lg border border-border p-4 sm:p-6" aria-labelledby="your-data-heading">
            <h2 id="your-data-heading" className="text-lg font-semibold">Your data</h2>
            <Link to="/business/export" className="flex min-h-11 items-center justify-between text-sm text-foreground">
              Download everything (CSV)
              <span className="text-muted-foreground">›</span>
            </Link>
            <button type="button" disabled className="flex min-h-11 w-full items-center justify-between text-left text-sm text-money-negative disabled:opacity-60">
              Delete account and data
              <span>›</span>
            </button>
            <p className="text-xs text-muted-foreground">
              Bank connections are managed on Accounts. Records only. We do not file. Account deletion isn’t available yet.
            </p>
          </section>
        </>
      )}
    </section>
  );
}
